// soccer_admin/src/pages/SupportMessages.jsx
import React, { useEffect, useState } from 'react';
import { fetchSupportMessages, sendAdminReply } from '../services/api';
import { 
  EnvelopeIcon, ChatBubbleLeftRightIcon, UserIcon, 
  CalendarIcon, CheckCircleIcon, XCircleIcon, ArrowPathIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';

const SupportMessages = () => {
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const res = await fetchSupportMessages();
      setMessages(res.data);
    } catch (err) {
      console.error(err);
      alert('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (messageId) => {
    const reply = replyText[messageId];
    if (!reply || !reply.trim()) {
      alert('Please enter a reply');
      return;
    }
    setSending(prev => ({ ...prev, [messageId]: true }));
    try {
      await sendAdminReply(messageId, reply);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? { ...msg, admin_reply: reply, is_answered: true }
            : msg
        )
      );
      setReplyText(prev => ({ ...prev, [messageId]: '' }));
      setExpandedId(null);
    } catch (err) {
      console.error(err);
      alert('Failed to send reply');
    } finally {
      setSending(prev => ({ ...prev, [messageId]: false }));
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
        <span className="ml-3 text-white text-lg">Loading messages...</span>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-16">
        <ChatBubbleLeftRightIcon className="h-16 w-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400 text-lg">No support messages yet.</p>
        <p className="text-gray-500 text-sm">When players send messages, they will appear here.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <ChatBubbleLeftRightIcon className="h-8 w-8 text-yellow-400" />
          Support Messages
        </h1>
        <p className="text-gray-400 mt-2">Manage and respond to player support requests</p>
      </div>

      {/* Desktop Grid (2 columns) */}
      <div className="hidden lg:grid lg:grid-cols-2 gap-6">
        {messages.map((msg) => (
          <MessageCard
            key={msg.id}
            message={msg}
            replyText={replyText[msg.id] || ''}
            setReplyText={(text) => setReplyText(prev => ({ ...prev, [msg.id]: text }))}
            onReply={() => handleReply(msg.id)}
            isSending={sending[msg.id]}
          />
        ))}
      </div>

      {/* Mobile/Tablet List (cards) */}
      <div className="lg:hidden space-y-6">
        {messages.map((msg) => (
          <div key={msg.id} className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-lg">
            {/* Header */}
            <div 
              className="p-4 cursor-pointer hover:bg-gray-700/50 transition-colors"
              onClick={() => toggleExpand(msg.id)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-yellow-400 font-semibold">{msg.username}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${msg.is_answered ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'}`}>
                      {msg.is_answered ? 'Answered' : 'Pending'}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mt-1 line-clamp-1">{msg.subject}</p>
                  <p className="text-gray-500 text-xs mt-1">{new Date(msg.created_at).toLocaleString()}</p>
                </div>
                <div className="text-gray-400">
                  <svg className={`w-5 h-5 transition-transform ${expandedId === msg.id ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Expanded Content */}
            {expandedId === msg.id && (
              <div className="p-4 pt-0 border-t border-gray-700">
                <div className="mb-3">
                  <p className="text-gray-300 text-sm font-medium">Message:</p>
                  <p className="text-white mt-1 whitespace-pre-wrap">{msg.message}</p>
                </div>
                
                {msg.admin_reply && (
                  <div className="mb-4 bg-gray-700/30 rounded-lg p-3 border-l-4 border-green-500">
                    <p className="text-green-400 text-sm font-semibold">Admin Reply:</p>
                    <p className="text-gray-200 mt-1">{msg.admin_reply}</p>
                  </div>
                )}

                {!msg.is_answered && (
                  <div className="mt-3">
                    <textarea
                      placeholder="Write your reply..."
                      value={replyText[msg.id] || ''}
                      onChange={(e) => setReplyText(prev => ({ ...prev, [msg.id]: e.target.value }))}
                      className="w-full bg-gray-700 text-white p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                      rows="3"
                    />
                    <div className="flex justify-end mt-2">
                      <button
                        onClick={() => handleReply(msg.id)}
                        disabled={sending[msg.id]}
                        className="bg-yellow-500 hover:bg-yellow-600 text-black font-medium px-4 py-2 rounded-lg transition disabled:opacity-50 flex items-center gap-2 text-sm"
                      >
                        {sending[msg.id] ? (
                          <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Sending...</>
                        ) : (
                          <><PaperAirplaneIcon className="h-4 w-4" /> Send Reply</>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// MessageCard component for desktop view
const MessageCard = ({ message, replyText, setReplyText, onReply, isSending }) => {
  const [isReplying, setIsReplying] = useState(false);

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden shadow-lg hover:shadow-yellow-500/10 transition-shadow">
      <div className="p-5">
        {/* User info header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-gray-700 rounded-full p-2">
              <UserIcon className="h-5 w-5 text-yellow-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">{message.username}</h3>
              <p className="text-gray-400 text-sm">{message.email}</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
            message.is_answered ? 'bg-green-600/20 text-green-400' : 'bg-red-600/20 text-red-400'
          }`}>
            {message.is_answered ? <CheckCircleIcon className="h-3 w-3" /> : <XCircleIcon className="h-3 w-3" />}
            {message.is_answered ? 'Answered' : 'Pending'}
          </span>
        </div>

        {/* Subject & Message */}
        <div className="mb-4">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <EnvelopeIcon className="h-4 w-4" />
            <span className="font-medium">Subject:</span>
          </div>
          <p className="text-white font-medium">{message.subject}</p>
        </div>

        <div className="mb-4">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
            <ChatBubbleLeftRightIcon className="h-4 w-4" />
            <span className="font-medium">Message:</span>
          </div>
          <p className="text-gray-300 whitespace-pre-wrap">{message.message}</p>
        </div>

        {/* Timestamp */}
        <div className="flex items-center gap-1 text-gray-500 text-xs mb-4">
          <CalendarIcon className="h-3 w-3" />
          <span>{new Date(message.created_at).toLocaleString()}</span>
        </div>

        {/* Admin Reply (if exists) */}
        {message.admin_reply && (
          <div className="mb-4 bg-gray-700/30 rounded-lg p-3 border-l-4 border-green-500">
            <p className="text-green-400 text-sm font-semibold flex items-center gap-1">
              <CheckCircleIcon className="h-4 w-4" />
              Admin Reply:
            </p>
            <p className="text-gray-200 mt-1 text-sm">{message.admin_reply}</p>
          </div>
        )}

        {/* Reply section (if not answered) */}
        {!message.is_answered && (
          <div>
            {!isReplying ? (
              <button
                onClick={() => setIsReplying(true)}
                className="w-full border border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 font-medium py-2 rounded-lg transition flex items-center justify-center gap-2"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                Reply to {message.username}
              </button>
            ) : (
              <div className="mt-3">
                <textarea
                  placeholder="Write your reply..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  className="w-full bg-gray-700 text-white p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm"
                  rows="3"
                  autoFocus
                />
                <div className="flex justify-end gap-2 mt-2">
                  <button
                    onClick={() => setIsReplying(false)}
                    className="px-4 py-2 rounded-lg bg-gray-700 text-gray-300 hover:bg-gray-600 transition text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={onReply}
                    disabled={isSending}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black font-medium px-4 py-2 rounded-lg transition disabled:opacity-50 flex items-center gap-2 text-sm"
                  >
                    {isSending ? <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Sending</> : <><PaperAirplaneIcon className="h-4 w-4" /> Send</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportMessages;